import React, { useMemo, useState } from "react";
import "../styles/Medicationview.css";

import activeIcon from "../assets/medication/activemedication.svg";
import adherenceIcon from "../assets/medication/adherence.svg";
import missedIcon from "../assets/medication/misseddose.svg";
import needsIcon from "../assets/medication/needsdose.svg";


function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function fmtTime(t) {
  return t || "—";
}

function countStatuses(logs) {
  let taken = 0,
    missed = 0,
    upcoming = 0;
  logs.forEach((l) => {
    if (l.status === "taken") taken++;
    else if (l.status === "missed") missed++;
    else upcoming++;
  });
  return { taken, missed, upcoming };
}

function getFilteredLogs(med) {
  if (!med.start_date) return med.logs;
  return med.logs.filter((l) => l.date >= med.start_date);
}

function getAdherencePct(med) {
  const filtered = getFilteredLogs(med);
  const s = countStatuses(filtered);
  const active = s.taken + s.missed;
  return active > 0 ? Math.round((s.taken / active) * 100) : null;
}

function isActiveMed(med) {
  const today = todayKey();
  if (med.start_date && today < med.start_date) return false;
  if (med.end_date && today > med.end_date) return false;
  return true;
}


function StatCard({
  icon,
  label,
  value,
  sublabel,
  tone,
  active,
  onClick,
}) {
  return (
    <div
      className={`mv-stat-card mv-tone-${tone} ${
        active ? "mv-stat-card-active" : ""
      }`}
      onClick={onClick}
    >
      <div className="mv-stat-card-top">
        <span className="mv-stat-card-label">{label}</span>

        <div className="mv-stat-card-icon">
          {icon ? (
            <img src={icon} alt="" />
          ) : (
            <span className="mv-icon-fallback" />
          )}
        </div>
      </div>

      <div className="mv-stat-card-value">
        {value}
      </div>

      {sublabel && (
        <div className="mv-stat-card-sub">
          {sublabel}
        </div>
      )}
    </div>
  );
}

export default function MedicationView({
  patient,
  programData,
  icons = {}, // { active, adherence, missed, needsAttention }
}) {
  const [search, setSearch] = useState("");

  const [selectedCard, setSelectedCard] = useState("adherence");

  const meds = programData?.medication_summary ?? [];

  // ---- Table rows (filtered by search) ----
  // Hooks must run unconditionally, before any early returns below.
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return meds.filter((med) => {
      if (!q) return true;
      return (
        med.medicine_name?.trim().toLowerCase().includes(q) ||
        med.reason?.trim().toLowerCase().includes(q)
      );
    });
  }, [meds, search]);

  if (!programData) return <div className="mv-center-msg">Loading…</div>;
  if (meds.length === 0)
    return <div className="mv-center-msg">No medication records found.</div>;

  const today = todayKey();

  // ---- Header stats ----
  let totalTaken = 0,
    totalMissed = 0;
  let missedToday = 0;
  let needsAttention = 0;

  meds.forEach((med) => {
    const filtered = getFilteredLogs(med);
    const s = countStatuses(filtered);
    totalTaken += s.taken;
    totalMissed += s.missed;

    filtered.forEach((l) => {
      if (l.date === today && l.status === "missed") missedToday++;
    });

    const pct = getAdherencePct(med);
    if (pct !== null && pct < 60) needsAttention++;
  });

  const activeDoses = totalTaken + totalMissed;
  const overallAdherence =
    activeDoses > 0 ? Math.round((totalTaken / activeDoses) * 100) : 0;

  return (
    <div className="mv-root">
      <div className="mv-header-row">
        <div>
          <h2 className="mv-title">Medication Overview</h2>
          <p className="mv-subtitle">
            Track Prescribed medicines, adherence, dose timing and follow-up
            actions
          </p>
        </div>
      </div>

      <div className="mv-stats-grid">
        <StatCard
          icon={activeIcon}
          tone="active"
          label="Active Medications"
          value={meds.length}
          sublabel={`out of ${meds.length} prescribed`}
          active={selectedCard === "active"}
          onClick={() => setSelectedCard("active")}
        />
        <StatCard
          icon={adherenceIcon}
          tone="adherence"
          label="Adherence (last 7 days)"
          value={`${overallAdherence}%`}
          sublabel="active doses only"
          active={selectedCard==="adherence"}
          onClick={() => setSelectedCard("adherence")}
        />
        <StatCard
          icon={missedIcon}
          tone="missed"
          label="Missed Doses ( Today )"
          value={missedToday}
          sublabel={`out of ${meds.length} doses`}
          active={selectedCard==="missed"}
          onClick={() => setSelectedCard("missed")}
        />
        <StatCard
          icon={needsIcon}
          tone="attention"
          label="Needs Attention"
          value={needsAttention}
          sublabel="medication needs review"
          active={selectedCard==="attention"}
          onClick={() => setSelectedCard("attention")}
        />
      </div>

      <div className="mv-search-wrap">
        <span className="mv-search-icon">⌕</span>
        <input
          type="text"
          className="mv-search-input"
          placeholder="Search medication by name or condition"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="mv-table-wrap">
        <table className="mv-table">
          <thead>
            <tr>
              <th>Medication</th>
              <th>Dose &amp; Frequency</th>
              <th>Timing</th>
              <th>Progress</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((med) => {
              const filtered = getFilteredLogs(med);
              const s = countStatuses(filtered);
              const timeSlots =
                [...new Set(filtered.map((l) => l.time_of_day))].join(", ") ||
                "—";
              const nextTime = filtered.find((l) => l.date === today)?.time;
              const foodNote =
                med.logs[0]?.after_food != null
                  ? med.logs[0].after_food
                    ? "After Breakfast"
                    : "Before Breakfast"
                  : "—";
              const active = isActiveMed(med);
              const progressLabel =
                s.missed === 0 ? "On-Time" : `${s.missed} Missed`;

              return (
                <tr key={med.med_id}>
                  <td>
                    <div className="mv-med-cell">
                      <img
                        src={activeIcon}
                        alt="Medication"
                        className="mv-med-icon"
                      />
                      <div>
                        <div className="mv-med-name">{med.medicine_name}</div>
                        <div className="mv-med-reason">
                          For {med.reason || "—"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="mv-dose-cell">
                      <div>{med.dosage}</div>
                      <div className="mv-dose-freq">{med.frequency}</div>
                    </div>
                  </td>
                  <td>
                    <div className="mv-timing-cell">
                      <div>{foodNote}</div>
                      <div className="mv-timing-sub">
                        {timeSlots !== "—" ? timeSlots : ""}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="mv-progress-cell">
                      <div>Today {fmtTime(nextTime)}</div>
                      <div
                        className={`mv-progress-sub ${
                          s.missed === 0 ? "mv-progress-ok" : "mv-progress-bad"
                        }`}
                      >
                        {progressLabel}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span
                      className={`mv-status-pill ${
                        active ? "mv-status-active" : "mv-status-inactive"
                      }`}
                    >
                      {active ? "Active" : "Inactive"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}